// src/components/AgedReceivables.js
// Aged Receivables (Aged Debtors): every learner's outstanding fee balance split
// into Current / 31-60 / 61-90 / 90+ day buckets as of a chosen date.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow,
  TableCell, TextField, Alert, CircularProgress, IconButton
} from '@mui/material';
import { Home, Print, HourglassBottom, Description } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';
import CompanyHeader from './CompanyHeader';
import { useCompany } from '../hooks/useCompany';
import { downloadCsv } from '../utils/csvExport';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const todayStr = () => new Date().toISOString().split('T')[0];

const AgedReceivables = () => {
  const navigate = useNavigate();
  const company = useCompany();
  const [asOf, setAsOf]       = useState(todayStr());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const run = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/api/finance/reports/aged-receivables`, { headers: hdrs(), params: { asOf } });
      setData(data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load aged receivables'); }
    finally { setLoading(false); }
  }, [asOf]);

  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportExcel = () => {
    if (!data) return;
    const t = data.totals;
    const rows = [
      [company?.name || 'AGED RECEIVABLES'],
      ['AGED RECEIVABLES'],
      [`As of ${asOf}`],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [],
      ['Adm No', 'Name', 'Class', 'Stage', 'Current', '31-60', '61-90', 'Over 90', 'Total'],
      ...data.rows.map(r => [r.admNo, r.name, r.classCode, r.stage, r.current, r.d31_60, r.d61_90, r.over90, r.total]),
      [],
      ['', '', '', 'TOTALS', t.current, t.d31_60, t.d61_90, t.over90, t.total],
    ];
    downloadCsv(rows, `aged_receivables_${asOf}.csv`);
  };

  const t = data?.totals;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff !important; } .ar-card { box-shadow:none !important; } }`}</style>
      <Box display="flex" gap={1.5} mb={2} className="no-print" alignItems="center" flexWrap="wrap">
        <TextField type="date" label="As of" value={asOf} size="small"
          onChange={e => setAsOf(e.target.value)} InputLabelProps={{ shrink: true }} />
        <IconButton onClick={run} disabled={loading} color="primary" sx={{ bgcolor: '#c6282815' }}>
          {loading ? <CircularProgress size={20} /> : <HourglassBottom />}
        </IconButton>
        <Box flex={1} />
        <IconButton onClick={exportExcel} title="Export to Excel" disabled={!data}
          sx={{ bgcolor: 'background.paper', color: 'success.main', '&:hover': { bgcolor: 'grey.100' } }}><Description /></IconButton>
        <IconButton onClick={() => window.print()} title="Print" disabled={!data}
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Print /></IconButton>
        <IconButton onClick={() => navigate('/finance')} title="Finance Home"
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Home /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card elevation={3} className="ar-card">
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <CompanyHeader title="AGED RECEIVABLES" subtitle={`As of ${asOf}`} />
          {data && (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Adm No</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Class</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Current</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>31–60</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>61–90</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Over 90</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.rows.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center">No outstanding balances</TableCell></TableRow>
                  ) : data.rows.map(r => (
                    <TableRow key={r.admNo} hover onClick={() => navigate(`/ledger/${r.admNo}`)}
                      sx={{ cursor: 'pointer', '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                      <TableCell>{r.admNo}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{r.classCode || '-'}</TableCell>
                      <TableCell align="right">{fmt(r.current)}</TableCell>
                      <TableCell align="right">{fmt(r.d31_60)}</TableCell>
                      <TableCell align="right">{fmt(r.d61_90)}</TableCell>
                      <TableCell align="right" sx={{ color: r.over90 > 0 ? 'error.main' : 'inherit' }}>{fmt(r.over90)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(r.total)}</TableCell>
                    </TableRow>
                  ))}
                  {data.rows.length > 0 && (
                    <TableRow sx={{ bgcolor: 'grey.200' }}>
                      <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>TOTALS ({data.count})</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(t.current)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(t.d31_60)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(t.d61_90)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>{fmt(t.over90)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.05rem' }}>{sym()} {fmt(t.total)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Aged by invoice date. Click a learner to open their statement.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AgedReceivables;
